// GM combat navigation and turn-flow handlers. Public functions are window-attached below.

import { claimFt3dInteractionMode, releaseFt3dInteractionMode } from '../shared/ft3d-interaction-mode';

/** StatusEffect 'dying' (includes/catalogs/StatusEffect.php). */
const DYING_STATUS_ID = '8AE129AF-52D8-CCE8-706A-85767DB52740';

/**
 * The exit-combat warning's rule, in the browser: every undeleted PLAYER CHARACTER of the held encounter carrying a Dying status row
 * (has_status_effect over statuses.target — stored rows, no vitality-band injection), by player name. null when the page does not
 * hold the encounter (the confirm is then skipped, as the old pre-check's failure path did).
 */
function dyingPlayerCharacters(encounterId: string): string[] | null {
	const encounter = (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache?.get(encounterId)
		?? (window as unknown as { FateTide?: { encounterCache?: Map<string, unknown> } }).FateTide?.encounterCache?.get(encounterId.toUpperCase());
	const roster = (encounter as { encounter_creatures_arr?: unknown[] } | undefined)?.encounter_creatures_arr;
	if (!Array.isArray(roster)) return null;
	const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
	const names: string[] = [];
	for (const raw of roster) {
		const row = raw as Record<string, any>;
		const ec = (row?.encounter_creatures ?? {}) as Record<string, unknown>;
		if (Number(scalar(ec.encounter_creature_deleted) ?? 0) !== 0) continue;
		const type = String(scalar(ec.item_type) ?? scalar(row?.items?.item_type) ?? '').toLowerCase();
		if (type !== 'player_character') continue;
		const statuses = typeof ftCreatureStatuses === 'function' ? (ftCreatureStatuses(row, 'target') as unknown[]) : [];
		const dying = statuses.some((s) => {
			const st = s as Record<string, any>;
			return String(scalar(st?.status_item_id) ?? st?.status_arr?.items?.item_id ?? st?.status_arr?.item_id ?? '').toUpperCase() === DYING_STATUS_ID;
		});
		if (!dying) continue;
		names.push(String(scalar(row?.player_characters?.player_name) ?? scalar(row?.items?.item_name) ?? scalar(ec.encounter_creature_name) ?? 'Player character'));
	}
	return names;
}

async function exitCombat() {

    // OPEN SIDE LOG FOR RESULT ROLL
    openSideLog();

    // LET REFRESH KNOW
    ftDebugLog('combat', { phase: 'exitCombat' });
	const sl = getSidelogContext();
	if (!sl) return;

	try {
		// Decided from the encounter this page holds (2026-09-30: the exit_combat_dying_check_inc.php server pre-check is gone).
		const pre = dyingPlayerCharacters(String(sl.encounter_id));
		if (pre && pre.length) {
			const list = pre.join(', ');
			const msg =
				'One or more characters are dying (' +
				list +
				'). Leaving combat switches to out-of-combat time: each campaign round is 6 seconds, and bleeding / dying resolution runs when time is pushed — unstabilized characters can die. Exit combat anyway?';
			if (!window.confirm(msg)) {
				return;
			}
		}
	} catch (e) {
		console.warn('exit combat dying precheck failed', e);
	}

	// T3 (2026-09-11): the combat boundary is the client-truth lane — the ONLY path (2026-09-30: the legacy exit_combat_inc.php
	// fallback is gone; one copy of the boundary). 'handled' = committed (or settled through its receipt); null = a gate / a definite
	// refusal, already told to the GM by the lane — nothing changed, so stop here.
	let truthHandled = false;
	try {
		const entry = (window as unknown as { ftTryCommitExitCombat?: (input: { campaignId: string; encounterId: string; rollPrivate?: number }) => Promise<'handled' | null> }).ftTryCommitExitCombat;
		if (typeof entry === 'function') truthHandled = (await entry({ campaignId: String(sl.campaign_id), encounterId: String(sl.encounter_id), rollPrivate: Number(sl.roll_private ?? 2) })) === 'handled';
		else console.warn('exit combat: the combat-truth lane is not loaded');
	} catch (error) { console.warn('exit combat: client-truth entry threw after a dispatch may have happened', error); truthHandled = true; }
	if (!truthHandled) return;

	// fix time
	(document.getElementById('play_campaign_time') as HTMLElement).style.display = 'none';
	(document.getElementById('pause_campaign_time') as HTMLElement).style.display = 'block';
	(document.getElementById('btn_in_combat_time_control') as HTMLElement).style.display = 'none';
	(document.getElementById('div_time_moving') as HTMLElement).style.display = 'block';

	document.querySelectorAll<HTMLElement>('.tr_campaign_time_control').forEach((element) => {
		element.style.display = 'block';
	});
	setMyCampaignFastForwardWrapHidden(false);

	syncMyCampaignTimeSpinnerSlot();
	syncMyCampaignPlayPauseStopCluster();

}

async function endTurn(casterId?: string) {

	const sl = getSidelogContext();
	const encounterId = String(sl?.encounter_id ?? '').trim();
	if (!encounterId) { displayWarning('End Turn encounter context is unavailable.'); return; }
	// Direct controls bind their subject at paint time; inspection/caster churn must
	// not turn a click on your own halo into a request for somebody else's turn.
	const caster_id = casterId || ftGetActiveCasterId();
	if (!caster_id) { displayWarning('Choose a creature before ending its turn.'); return; }

	// Every End Turn is decided by the browser turn lane.
	const tryEndTurn = (window as unknown as { ftTryCommitEndTurn?: (input: { encounterId: string; actorId: string; rollPrivate?: number }) => Promise<'handled' | 'refused'> }).ftTryCommitEndTurn;
	if (typeof tryEndTurn !== 'function') { displayWarning('End Turn browser lane is not loaded.'); return; }
	try {
		const outcome = await tryEndTurn({ encounterId, actorId: caster_id, rollPrivate: Number(sl?.roll_private ?? 2) });
		if (outcome !== 'handled' && outcome !== 'refused') displayWarning('End Turn returned an unknown outcome. Check the turn state and try again.');
	} catch (error) { displayWarning('End Turn could not be committed: ' + String(error)); }
	finally { cancelTargetDraft('end-turn'); }

}

const TURN_JUMP_PICK_PANEL_ID = 'ft_turn_jump_pick_panel';
let turnJumpPickActive = false;
let turnJumpPickBusy = false;

function turnJumpFrame(): HTMLIFrameElement | null {
	return document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
}

function postTurnJumpPickMode(active: boolean): void {
	turnJumpFrame()?.contentWindow?.postMessage(
		{ type: 'ft3d-campaign-creature-pick-mode-v1', payload: { active } },
		window.location.origin,
	);
}

function renderTurnJumpPicker(): void {
	let panel = document.getElementById(TURN_JUMP_PICK_PANEL_ID);
	if (!panel) {
		panel = document.createElement('div');
		panel.id = TURN_JUMP_PICK_PANEL_ID;
		panel.className = 'alert alert-info position-fixed bottom-0 start-50 translate-middle-x m-3 shadow d-flex align-items-center gap-3';
		panel.style.zIndex = '2147483000';
		panel.setAttribute('role', 'status');
		document.body.appendChild(panel);
		panel.addEventListener('click', (event) => {
			const target = event.target instanceof Element
				? event.target.closest('[data-ft-turn-jump-cancel]')
				: null;
			if (target) cancelGoToCreatureTurnPick();
		});
	}
	panel.innerHTML = `<span>${turnJumpPickBusy
		? 'Moving to that creature\'s turn&hellip;'
		: 'Select a creature in the Scene or Combat Widget to jump to its turn.'}</span>
		<button type="button" class="btn btn-sm btn-outline-dark" data-ft-turn-jump-cancel ${turnJumpPickBusy ? 'disabled' : ''}>Cancel</button>`;
}

function cancelGoToCreatureTurnPick(): void {
	if (!turnJumpPickActive) {
		releaseFt3dInteractionMode('turn-jump');
		return;
	}
	turnJumpPickActive = false;
	turnJumpPickBusy = false;
	window.removeEventListener('message', onTurnJumpPickMessage);
	window.removeEventListener('keydown', onTurnJumpPickKeyDown, true);
	postTurnJumpPickMode(false);
	document.getElementById(TURN_JUMP_PICK_PANEL_ID)?.remove();
	releaseFt3dInteractionMode('turn-jump');
}

function onTurnJumpPickKeyDown(event: KeyboardEvent): void {
	if (event.key !== 'Escape' || !turnJumpPickActive || turnJumpPickBusy) return;
	event.preventDefault();
	cancelGoToCreatureTurnPick();
}

function onTurnJumpPickMessage(event: MessageEvent): void {
	if (!turnJumpPickActive || event.origin !== window.location.origin
		|| event.source !== turnJumpFrame()?.contentWindow) return;
	const envelope = event.data as { type?: unknown; payload?: { entityId?: unknown } } | null;
	if (envelope?.type !== 'ft3d-campaign-creature-pick-v1') return;
	void pickCreatureTurn(String(envelope.payload?.entityId ?? ''));
}

async function pickCreatureTurn(entityId: string): Promise<boolean> {
	if (!turnJumpPickActive || turnJumpPickBusy) return false;
	const targetId = String(entityId ?? '').trim().toUpperCase();
	if (!checkIfValidUUID(targetId)) return false;
	const sl = getSidelogContext();
	if (!sl) return false;

	turnJumpPickBusy = true;
	renderTurnJumpPicker();
	try {
		// The turn jump is the client-truth lane — the ONLY path (2026-09-30: go_to_creature_turn_inc.php is gone). null = a gate / a
		// definite refusal, already told to the GM by the lane.
		try {
			const entry = (window as unknown as { ftTryCommitGotoTurn?: (input: { campaignId: string; encounterId: string; targetId: string; rollPrivate?: number }) => Promise<'handled' | null> }).ftTryCommitGotoTurn;
			if (typeof entry === 'function') await entry({ campaignId: String(sl.campaign_id), encounterId: String(sl.encounter_id), targetId, rollPrivate: Number(sl.roll_private ?? 2) });
			else console.warn('turn jump: the combat-truth lane is not loaded');
		} catch (error) { console.warn('turn jump: client-truth entry threw after a dispatch may have happened', error); }
	} finally {
		cancelGoToCreatureTurnPick();
	}
	return true;
}

/** Arm a one-shot creature picker. The old implementation consumed global target_id immediately,
 * which forced the GM to target first and made neutral combat-widget clicks create lasting targets. */
function goToCreatureTurn(): void {
	if (turnJumpPickActive) {
		cancelGoToCreatureTurnPick();
		return;
	}
	claimFt3dInteractionMode('turn-jump', cancelGoToCreatureTurnPick);
	window.ftSetTargetSelection?.('', 'turn-jump-pick-arm');
	turnJumpPickActive = true;
	window.addEventListener('message', onTurnJumpPickMessage);
	window.addEventListener('keydown', onTurnJumpPickKeyDown, true);
	postTurnJumpPickMode(true);
	renderTurnJumpPicker();
}

function routeTurnJumpCreature(entityId: string): boolean {
	if (!turnJumpPickActive) return false;
	void pickCreatureTurn(entityId);
	return true;
}

async function goToEncounter(go_to_encounter: unknown): Promise<void> {
	await travelToEncounter(go_to_encounter);
}

async function goToInventoryLink(
	go_to_inventory_link: unknown,
	go_to_encounter: unknown
): Promise<void> {
	if (!checkIfValidUUID(go_to_inventory_link)) return;
	await travelToEncounter(go_to_encounter, String(go_to_inventory_link));
}

async function travelToEncounter(destination: unknown, anchor?: string): Promise<void> {
	if (!checkIfValidUUID(destination)) return;
	const relocate = (window as Window & {
		ftGeometryRelocation?: (data: Record<string, unknown>) => Promise<Record<string, unknown>>;
	}).ftGeometryRelocation;
	if (!relocate) { displayWarning('Transfer unavailable: browser relocation is not loaded.'); return; }
	const result = await relocate({
		campaign_id: window.ftGetCampaignId?.() || getSidelogContext()?.campaign_id || '',
		caster_id: ftGetActiveCasterId(),
		relocation_route: 'go_to_encounter_inc.php',
		go_to_encounter: String(destination),
		...(anchor ? { go_to_inventory_link: anchor } : {}),
	});
	if (result.ok !== true) return;
	const encounterId = String(result.destinationEncounterId);
	window.ftSetActiveEncounterId?.(encounterId, anchor ? 'combat-go-to-inventory-link' : 'combat-go-to-encounter');
	await syncTimeControlsForEncounterId(encounterId);
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. All five were globals and
// have out-of-bundle consumers reached at runtime: exitCombat / goToCreatureTurn (turn-widget gm-controls
// onclick), goToEncounter (PHP onclick + table_item_object_inc.php), goToInventoryLink (table_item_object_inc.php),
// endTurn (Action Bar End Turn handler).
// ===========================================================================================
declare global {
    interface Window {
        exitCombat: typeof exitCombat;
        endTurn: typeof endTurn;
        goToCreatureTurn: typeof goToCreatureTurn;
        cancelGoToCreatureTurnPick: typeof cancelGoToCreatureTurnPick;
        ftTurnJumpPickEntity: typeof routeTurnJumpCreature;
        ftCancelCombatPick?: () => void;
        ftAbilityTargetingCancel?: () => boolean;
        ftSetTargetSelection?: (selection: string, source?: string) => void;
        goToEncounter: typeof goToEncounter;
        goToInventoryLink: typeof goToInventoryLink;
    }
}

Object.assign(window, {
    exitCombat,
    endTurn,
    goToCreatureTurn,
    cancelGoToCreatureTurnPick,
    ftTurnJumpPickEntity: routeTurnJumpCreature,
    goToEncounter,
    goToInventoryLink,
});

export {};
